import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { UserCircleIcon, KeyIcon, BellIcon } from '@heroicons/react/24/outline';
import { authAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const { user, updateUser } = useAuthStore();

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-slate-400 mt-1">Manage your account and preferences</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-2">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'profile' ? 'bg-primary-500/20 text-primary-400' : 'text-slate-400 hover:text-white'
          }`}
        >
          <UserCircleIcon className="w-4 h-4 inline mr-2" />
          Profile
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'security' ? 'bg-primary-500/20 text-primary-400' : 'text-slate-400 hover:text-white'
          }`}
        >
          <KeyIcon className="w-4 h-4 inline mr-2" />
          Security
        </button>
      </div>

      {activeTab === 'profile' && <ProfileTab user={user} />}
      {activeTab === 'security' && <SecurityTab />}
    </div>
  );
};

const ProfileTab = ({ user }) => {
  return (
    <div className="max-w-xl">
      <div className="card space-y-6">
        <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">
          Profile Information
        </h2>

        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center">
            <span className="text-2xl font-bold text-primary-400">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{user?.name}</h3>
            <p className="text-slate-400">{user?.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs bg-primary-500/20 text-primary-400">
              {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'User'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input type="text" value={user?.name || ''} disabled className="input opacity-60" />
          </div>
          <div>
            <label className="label">Email</label>
            <input type="email" value={user?.email || ''} disabled className="input opacity-60" />
          </div>
          <div>
            <label className="label">Organization</label>
            <input type="text" value={user?.tenant?.name || ''} disabled className="input opacity-60" />
          </div>
        </div>

        <p className="text-sm text-slate-500">
          Contact your administrator to update your profile information.
        </p>
      </div>
    </div>
  );
};

const SecurityTab = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();
  const newPassword = watch('newPassword');

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await authAPI.changePassword(data.currentPassword, data.newPassword);
      toast.success('Password changed successfully');
      reset();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="card space-y-6">
        <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">
          Change Password
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Current Password</label>
            <input
              type="password"
              {...register('currentPassword', { required: 'Current password is required' })}
              className={`input ${errors.currentPassword ? 'input-error' : ''}`}
              placeholder="••••••••"
            />
            {errors.currentPassword && (
              <p className="mt-1 text-sm text-red-400">{errors.currentPassword.message}</p>
            )}
          </div>

          <div>
            <label className="label">New Password</label>
            <input
              type="password"
              {...register('newPassword', {
                required: 'New password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
              className={`input ${errors.newPassword ? 'input-error' : ''}`}
              placeholder="••••••••"
            />
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-400">{errors.newPassword.message}</p>
            )}
          </div>

          <div>
            <label className="label">Confirm New Password</label>
            <input
              type="password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: value => value === newPassword || 'Passwords do not match',
              })}
              className={`input ${errors.confirmPassword ? 'input-error' : ''}`}
              placeholder="••••••••"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-400">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="pt-2">
            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SettingsPage;
